import React, { useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "../theme";
import { Band, Card } from "../ui";

const Item = ({ faq, open, onToggle, index }) => (
  <Card
    bg={open ? rf.cream : rf.paper}
    shadow={open ? rf.shadow : rf.shadowSm}
    sx={{ mb: 2, overflow: "hidden", transition: "box-shadow 0.15s ease" }}
  >
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`faq-panel-${index}`}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: { xs: 2, md: 2.5 },
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
      }}
    >
      <Typography sx={{ fontFamily: rf.display, fontSize: { xs: 16, md: 17.5 }, fontWeight: 700, color: rf.ink }}>
        {faq.q}
      </Typography>
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: 30,
          height: 30,
          display: "grid",
          placeItems: "center",
          backgroundColor: open ? rf.coral : rf.amber,
          border: rf.lineThin,
          borderRadius: "8px",
          fontFamily: rf.display,
          fontWeight: 800,
          fontSize: 18,
          lineHeight: 1,
          color: rf.ink,
        }}
      >
        {open ? "−" : "+"}
      </Box>
    </Box>

    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          id={`faq-panel-${index}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
          style={{ overflow: "hidden" }}
        >
          <Typography
            sx={{
              fontFamily: rf.body,
              fontSize: 15.5,
              color: rf.inkSoft,
              lineHeight: 1.7,
              px: { xs: 2, md: 2.5 },
              pb: 2.5,
              pt: 0,
            }}
          >
            {faq.a}
          </Typography>
        </motion.div>
      )}
    </AnimatePresence>
  </Card>
);

export const FAQ = ({ faqs = [], loading }) => {
  const reduced = useReducedMotion();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <Band
      id="faq"
      band
      eyebrow="Questions people actually ask"
      title="The awkward ones, answered"
      lead="If something here contradicts what a salesperson told you elsewhere, this page is the one we stand behind."
      maxWidth="md"
    >
      <Box sx={{ textAlign: "left" }}>
        {loading && [0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 2 }} />)}

        {!loading && (
          <motion.div variants={stagger(0.05)} {...revealProps(reduced)}>
            {faqs.map((faq, i) => (
              <motion.div key={faq.q} variants={fadeUp}>
                <Item faq={faq} index={i} open={openIndex === i} onToggle={() => setOpenIndex(openIndex === i ? -1 : i)} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </Box>
    </Band>
  );
};

export default FAQ;
